package org.example.project.engineslot

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginekalender.EngineMonthCalendar
import org.example.project.settings.UISettings
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun SlotTopBarLayout(
    headline: String,
    address: String,
    actionBar: @Composable () -> Unit,
    topContent: @Composable () -> Unit
) {
    Div({
        classes(SlotStyles.topPanel)
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(10.px)
        }
    }) {
        SlotHeadline(headline, address)
        actionBar()
        topContent()
    }
}

@Composable
private fun SlotHeadline(
    headline: String,
    address: String
) {
    if (headline.isNotBlank()) {
        org.jetbrains.compose.web.dom.Span({ classes(SlotStyles.topHeadline) }) {
            org.jetbrains.compose.web.dom.Text(headline)
        }
    }
    if (address.isNotBlank()) {
        org.jetbrains.compose.web.dom.Span({ classes(SlotStyles.topAddress) }) {
            org.jetbrains.compose.web.dom.Text(address)
        }
    }
}

@Composable
fun SlotActionBarRow(
    leftContent: @Composable () -> Unit,
    centerContent: @Composable () -> Unit,
    rightContent: @Composable () -> Unit
) = SlotActionBarRow(leftContent, centerContent, rightContent, fullWidthCenter = false)

@Composable
fun SlotActionBarRow(
    leftContent: @Composable () -> Unit,
    centerContent: @Composable () -> Unit,
    rightContent: @Composable () -> Unit,
    fullWidthCenter: Boolean
) {
    Div({ classes(SlotStyles.actionBarRow); if (fullWidthCenter) style { gridTemplateColumns("minmax(0, 1fr)") } }) {
        if (!fullWidthCenter) Div({ classes(SlotStyles.actionBarLeft) }) {
            leftContent()
        }
        Div({ classes(SlotStyles.actionBarCenter) }) {
            centerContent()
        }
        if (!fullWidthCenter) Div({ classes(SlotStyles.actionBarRight) }) {
            rightContent()
        }
    }
}

@Composable
fun SlotInlineLocation(
    headline: String,
    address: String,
    dropdown: (@Composable () -> Unit)? = null
) {
    val companyText = headline.trim()
    val addressText = address.trim()
    if (companyText.isBlank() && addressText.isBlank() && dropdown == null) return

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(10.px)
            minWidth(0.px)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        if (companyText.isNotBlank()) {
            Span({
                style {
                    color(DashboardTheme.TEXT_LIGHT)
                    fontSize(15.px)
                    fontWeight("600")
                    property("white-space", "normal")
                    property("word-break", "keep-all")
                    minWidth(0.px)
                }
            }) {
                Text(companyText)
            }
        }
        if (dropdown != null) {
            if (companyText.isNotBlank()) {
                Span({
                    style {
                        color(DashboardTheme.TEXT_MUTED)
                        fontSize(15.px)
                        fontWeight("600")
                    }
                }) {
                    Text("|")
                }
            }
            dropdown()
        } else if (addressText.isNotBlank()) {
            if (companyText.isNotBlank()) {
                Span({
                    style {
                        color(DashboardTheme.TEXT_MUTED)
                        fontSize(15.px)
                        fontWeight("600")
                    }
                }) {
                    Text("|")
                }
            }
            Span({
                style {
                    color(DashboardTheme.TEXT_MUTED)
                    fontSize(15.px)
                    fontWeight("600")
                    property("white-space", "normal")
                    property("word-break", "keep-all")
                    minWidth(0.px)
                }
            }) {
                Text(addressText)
            }
        }
    }
}

@Composable
fun SlotField(label: String, content: @Composable () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
        }
    }) {
        Span({ classes(SlotStyles.label) }) { Text(label) }
        content()
    }
}

@Composable
fun SlotDatePager(
    selectedDate: String,
    onDateChange: (String) -> Unit
) {
    Div({ classes(SlotStyles.datePagerPlain) }) {
        SlotPagerArrow("<") { onDateChange(slotShiftDate(selectedDate, -1)) }
        Span({ classes(SlotStyles.datePagerValue) }) { Text(slotDisplayDate(selectedDate)) }
        SlotPagerArrow(">") { onDateChange(slotShiftDate(selectedDate, 1)) }
    }
}

@Composable
fun SlotCalendarPopup(
    selectedDate: String,
    onDateChange: (String) -> Unit,
    onClose: () -> Unit
) {
    PopupEngineCard(
        windowKey = "slot-kalender-popup",
        widthPx = 404,
        heightPx = 470,
        minWidthPx = 404,
        minHeightPx = 470,
        onClose = onClose
    ) {
        Div({
            classes(SlotStyles.calendarPopupBody)
        }) {
            Div({
                classes(SlotStyles.calendarPopupContent)
            }) {
                EngineMonthCalendar(
                    selectedDate = selectedDate,
                    onSelectDate = {
                        onDateChange(it)
                        onClose()
                    },
                    showFooter = false,
                    useWeekBorderLayout = false,
                    showSelectedDayBorder = true
                )
            }
        }
    }
}

@Composable
fun SlotPagerArrow(
    label: String,
    onClickAction: () -> Unit
) {
    var pressed by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    Span({
        classes(SlotStyles.datePagerArrow)
        if (pressed) classes(SlotStyles.datePagerArrowActive)
        onClick {
            pressed = true
            scope.launch {
                delay(150)
                pressed = false
            }
            onClickAction()
        }
    }) { Text(label) }
}

@Composable
fun SlotToolbarIcon(
    icon: String,
    active: Boolean = false,
    onClick: () -> Unit
) {
    var pressed by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    Div({
        classes(SlotStyles.toolbarIconButton)
        if (active || pressed) classes(SlotStyles.toolbarIconButtonActive)
        onClick {
            pressed = true
            scope.launch {
                delay(150)
                if (!active) pressed = false
            }
            onClick()
            if (!active) {
                scope.launch {
                    delay(170)
                    pressed = false
                }
            }
        }
    }) {
        Img(src = icon, attrs = {
            classes(SlotStyles.calendarIcon)
            style {
                property("filter", if (active || pressed) UISettings.ICON_FILTER_ACCENT else "brightness(0) invert(1)")
            }
        })
    }
}

@Composable
fun SlotToolbarAction(
    icon: String,
    label: String,
    active: Boolean = false,
    onClick: () -> Unit
) {
    var pressed by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    Div({
        classes(SlotStyles.toolbarAction)
        if (active || pressed) classes(SlotStyles.toolbarIconButtonActive)
        onClick {
            pressed = true
            scope.launch {
                delay(150)
                if (!active) pressed = false
            }
            onClick()
            if (!active) {
                scope.launch {
                    delay(170)
                    pressed = false
                }
            }
        }
    }) {
        Img(src = icon, attrs = {
            classes(SlotStyles.toolbarActionIcon)
            style {
                property("filter", if (active || pressed) UISettings.ICON_FILTER_ACCENT else "brightness(0) invert(1)")
            }
        })
        Span({ classes(SlotStyles.toolbarActionText) }) { Text(label) }
    }
}

@Composable
fun SlotToolbarSeparator() {
    Div({ classes(SlotStyles.toolbarActionSeparator) })
}
